import { memo, useId } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import InsightsPerLiftCard from "./InsightsPerLiftCard";
import InsightsSectionHeading from "./InsightsSectionHeading";

/**
 * Per-lift competition block (Powerlifting, Olympic, etc.): section title + one card per lift.
 *
 * @param {object} props
 * @param {object[]} props.lifts
 * @param {number} [props.syncRowIndex] — `data-insights-sync-row`; same index on this section in every week column for `useProgramInsightsCrossColumnRowSync` (see InsightsWeekColumn).
 * @param {string} [props.headingTitle] — override section title (e.g. Olympic lens).
 * @param {string} [props.headingInfo] — override (?) aria + tooltip for the section heading.
 */
function InsightsPowerliftingPerLiftSection({
  lifts,
  syncRowIndex,
  headingTitle,
  headingInfo,
  insightsIntensityMeasure = "rpe",
}) {
  const { t } = useTranslation("athlete-portal");

  /** Stable id for this column’s powerlifting section title. */
  const sectionTitleId = useId();

  const title =
    headingTitle ??
    t("programs.programInsights.panel.sectionPowerliftingPerLift");
  const info =
    headingInfo ??
    t("programs.programInsights.panel.powerliftingPerLiftSectionInfo");

  return (
    <section
      className="ProgramInsights-section ProgramInsights-section--liftGroup"
      aria-labelledby={sectionTitleId}
      data-insights-sync-row={syncRowIndex}
    >
      <InsightsSectionHeading
        titleId={sectionTitleId}
        title={title}
        infoAriaLabel={info}
      />
      <div className="ProgramInsights-liftCardList">
        {lifts.map((lift) => (
          <InsightsPerLiftCard
            key={lift.liftId}
            lift={lift}
            insightsIntensityMeasure={insightsIntensityMeasure}
          />
        ))}
      </div>
    </section>
  );
}

InsightsPowerliftingPerLiftSection.propTypes = {
  lifts: PropTypes.arrayOf(PropTypes.object).isRequired,
  insightsIntensityMeasure: PropTypes.oneOf(["rpe", "rir"]),
  syncRowIndex: PropTypes.number,
  headingTitle: PropTypes.string,
  headingInfo: PropTypes.string,
};

export default memo(InsightsPowerliftingPerLiftSection);
